import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import TabHeader from '../../Components/TabHeader/TabHeader';
import { useClient } from '../../../../api/api-client';
import Button from '../../../../components/ComponentsV1/Button';
import { Star } from '../../../../UiKit/Star/Star';
import TextEditor from '../../../../components/serverDrivenUIComponents/TextEditor';
import { SOP_ENDPOINTS, SKELETON_ROWS, isEditorEmpty } from './constants';
import { Skeleton } from '../../../../components/ComponentsV1/Skeleton/Skeleton';

const getTextField = (name, title, placeholder, value, onChange, required = true) => (
  <div className='tw-flex tw-flex-col tw-h-max tw-gap-5 tw-flex-1'>
    <div className='tw-w-full tw-text-grey-900 tw-font-medium tw-text-500'>
      {title} {required && <Star icon='*' />}
    </div>
    <div className='tw-flex tw-flex-col tw-w-full'>
      <input
        id={name}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className='tw-appearance-none tw-px-5 tw-py-2 tw-h-16 tw-rounded-lg tw-outline-0 focus:tw-border-none focus:tw-ring-[1.5px] tw-w-full focus:tw-ring-base-green focus:tw-border-base-green tw-border tw-border-solid tw-border-[#cfcfcf] tw-mr-6'
        autoComplete='off'
      />
    </div>
  </div>
);

const CreateSops = () => {
  const client = useClient();

  const [pid, setPid] = useState('');
  const [issue, setIssue] = useState('');
  const [subIssue, setSubIssue] = useState('');
  const [modelName, setModelName] = useState('');
  const [troubleshootingSteps, setTroubleshootingSteps] = useState('');
  const [editorKey, setEditorKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const isValid =
    pid.trim().length > 0 &&
    issue.trim().length > 0 &&
    subIssue.trim().length > 0 &&
    !isEditorEmpty(troubleshootingSteps);

  const handleReset = () => {
    setPid('');
    setIssue('');
    setSubIssue('');
    setModelName('');
    setTroubleshootingSteps('');
    setEditorKey((k) => k + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    setIsLoading(true);
    try {
      await client(SOP_ENDPOINTS.CREATE_OR_UPDATE, {
        data: {
          pid: pid.trim(),
          issue_type: issue.trim(),
          sub_issue_type: subIssue.trim(),
          model_name: modelName.trim(),
          troubleshooting_steps: troubleshootingSteps,
        },
        method: 'POST',
      });
      toast.success('SOP created successfully');
      handleReset();
    } catch (error) {
      toast.error(
        error?.error_message || error?.message || error?.detail || error?.error || 'Failed to create SOP',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10'>
      <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10 tw-bg-white tw-p-8 tw-rounded-2xl'>
        <TabHeader
          title='Create SOPs'
          subTitle='Create a new Standard Operating Procedure for a product, issue and sub-issue'
        />

        {isLoading && <Skeleton rows={SKELETON_ROWS} />}

        <form onSubmit={handleSubmit} style={{ display: isLoading ? 'none' : 'block' }}>
          <div className='tw-flex tw-w-full tw-h-max tw-gap-5'>
            {getTextField('pid', 'Product ID (PID)', 'Enter product ID', pid, setPid)}
            {getTextField('issue', 'Issue Type', 'Enter issue type', issue, setIssue)}
          </div>

          <div className='tw-flex tw-w-full tw-h-max tw-gap-5 tw-mt-8'>
            {getTextField('sub_issue', 'Sub-Issue Type', 'Enter sub-issue type', subIssue, setSubIssue)}
            {getTextField('model_name', 'Model Name', 'Enter model name', modelName, setModelName, false)}
          </div>

          <div className='tw-flex tw-flex-col tw-h-max tw-gap-5 tw-mt-8'>
            <div className='tw-w-full tw-text-grey-900 tw-font-medium tw-text-500'>
              SOPs <Star icon='*' />
            </div>
            <TextEditor
              key={editorKey}
              styleClasses='tw-w-full'
              inputKey='troubleshooting_steps'
              field={{ value: troubleshootingSteps }}
              saveOptions={(_, value) => setTroubleshootingSteps(value)}
              isEditable={true}
              hideSaveButton={true}
            />
          </div>

          <div className='tw-flex tw-gap-4 tw-mt-8'>
            <Button
              title='Create SOP'
              disabled={!isValid || isLoading}
            />
            <Button
              title='Cancel'
              type='outline'
              onClick={handleReset}
              disabled={isLoading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSops;
